import { parseDue, type Command, type Label, type Section, type Task } from "@todoer/shared";
import { cmd, uuid } from "./sync.js";

/**
 * Todoist-compatible CSV (the format Todoist uses for templates and exports):
 * TYPE,CONTENT,DESCRIPTION,PRIORITY,INDENT,AUTHOR,RESPONSIBLE,DATE,DATE_LANG,TIMEZONE,DURATION,DURATION_UNIT,DEADLINE,DEADLINE_LANG
 * PRIORITY 1 is the highest; INDENT 1 is a top-level task, 2 a sub-task, and so on.
 */
export const CSV_HEADER = ["TYPE", "CONTENT", "DESCRIPTION", "PRIORITY", "INDENT", "AUTHOR", "RESPONSIBLE", "DATE", "DATE_LANG", "TIMEZONE", "DURATION", "DURATION_UNIT", "DEADLINE", "DEADLINE_LANG"];

export function parseCsv(text: string): string[][] {
  const rows: string[][] = [];
  let row: string[] = [];
  let field = "";
  let quoted = false;
  const src = text.replace(/^﻿/, "");
  for (let i = 0; i < src.length; i++) {
    const ch = src[i]!;
    if (quoted) {
      if (ch === '"' && src[i + 1] === '"') { field += '"'; i++; }
      else if (ch === '"') quoted = false;
      else field += ch;
    } else if (ch === '"') quoted = true;
    else if (ch === ",") { row.push(field); field = ""; }
    else if (ch === "\n" || ch === "\r") {
      if (ch === "\r" && src[i + 1] === "\n") i++;
      row.push(field); field = "";
      if (row.some((f) => f !== "")) rows.push(row);
      row = [];
    } else field += ch;
  }
  row.push(field);
  if (row.some((f) => f !== "")) rows.push(row);
  return rows;
}

const cell = (v: string | number | null | undefined) => {
  const s = v == null ? "" : String(v);
  return /[",\n\r]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
};
export const toCsv = (rows: (string | number | null | undefined)[][]) => rows.map((r) => r.map(cell).join(",")).join("\r\n") + "\r\n";

/** Exports a project's open tasks (with sections and sub-tasks) as a Todoist-style CSV. */
export function projectToCsv(tasks: Task[], sections: Section[], labels: Label[]): string {
  const labelName = new Map(labels.map((l) => [l.id, l.name]));
  const rows: (string | number | null)[][] = [CSV_HEADER];
  const kids = (parentId: string | null, sectionId: string | null) =>
    tasks.filter((t) => t.parentId === parentId && (parentId !== null || t.sectionId === sectionId)).sort((a, b) => a.childOrder - b.childOrder);
  const emit = (t: Task, indent: number) => {
    const content = [t.content, ...t.labelIds.map((l) => labelName.get(l)).filter(Boolean).map((n) => `@${n}`)].join(" ");
    rows.push(["task", content, t.description, 5 - t.priority, indent, "", "", t.due?.string ?? "", t.due ? "en" : "", t.due?.timezone ?? "",
      t.durationMinutes ?? "", t.durationMinutes ? "minute" : "", t.deadline ?? "", t.deadline ? "en" : ""]);
    for (const k of kids(t.id, null)) emit(k, indent + 1);
  };
  for (const t of kids(null, null)) emit(t, 1);
  for (const s of [...sections].sort((a, b) => a.sectionOrder - b.sectionOrder)) {
    rows.push(["", "", "", "", "", "", "", "", "", "", "", "", "", ""]);
    rows.push(["section", s.name, "", "", "", "", "", "", "", "", "", "", "", ""]);
    for (const t of kids(null, s.id)) emit(t, 1);
  }
  return toCsv(rows);
}

/** Builds the commands that import a Todoist-style CSV into a project. */
export function csvToCommands(text: string, projectId: string, labels: Label[]): { commands: Command[]; tasks: number; sections: number } {
  const rows = parseCsv(text);
  const header = rows[0]?.map((h) => h.trim().toUpperCase()) ?? [];
  const hasHeader = header.includes("TYPE") && header.includes("CONTENT");
  const col = (name: string) => (hasHeader ? header.indexOf(name) : CSV_HEADER.indexOf(name));
  const body = hasHeader ? rows.slice(1) : rows;
  const get = (r: string[], name: string) => { const i = col(name); return i >= 0 ? (r[i] ?? "").trim() : ""; };

  const commands: Command[] = [];
  const labelIds = new Map(labels.map((l) => [l.name.toLowerCase(), l.id]));
  let sectionId: string | null = null;
  const stack: string[] = [];
  let order = 0;
  let taskCount = 0;
  let sectionCount = 0;

  for (const r of body) {
    const type = get(r, "TYPE").toLowerCase() || (hasHeader ? "" : "task");
    if (type === "section") {
      sectionId = uuid();
      commands.push(cmd("section_add", { id: sectionId, projectId, name: get(r, "CONTENT") || "Section", sectionOrder: ++sectionCount }));
      stack.length = 0;
      continue;
    }
    if (type !== "task") continue;
    let content = get(r, "CONTENT");
    if (!content) continue;
    const ids: string[] = [];
    content = content.replace(/(^|\s)@([\w-]+)/g, (_m, lead: string, name: string) => {
      const key = name.toLowerCase();
      let id = labelIds.get(key);
      if (!id) { id = uuid(); labelIds.set(key, id); commands.push(cmd("label_add", { id, name })); }
      ids.push(id);
      return lead;
    }).trim();
    const indent = Math.max(1, Number(get(r, "INDENT")) || 1);
    stack.length = Math.min(stack.length, indent - 1);
    const parentId = indent > 1 ? stack[indent - 2] ?? null : null;
    const pr = Number(get(r, "PRIORITY"));
    const dateText = get(r, "DATE");
    const due = dateText ? parseDue(dateText)?.due ?? null : null;
    const deadline = /^\d{4}-\d{2}-\d{2}$/.test(get(r, "DEADLINE")) ? get(r, "DEADLINE") : null;
    const dur = Number(get(r, "DURATION"));
    const id = uuid();
    commands.push(cmd("item_add", {
      id, projectId, sectionId: parentId ? null : sectionId, parentId, content,
      description: get(r, "DESCRIPTION"), priority: (pr >= 1 && pr <= 4 ? 5 - pr : 1) as 1 | 2 | 3 | 4,
      due, deadline, durationMinutes: dur > 0 ? Math.round(get(r, "DURATION_UNIT") === "day" ? dur * 1440 : dur) : null,
      labelIds: ids, childOrder: ++order,
    }));
    stack[indent - 1] = id;
    taskCount++;
  }
  return { commands, tasks: taskCount, sections: sectionCount };
}

export function download(filename: string, content: string, type = "text/csv;charset=utf-8"): void {
  const url = URL.createObjectURL(new Blob([content], { type }));
  const a = Object.assign(document.createElement("a"), { href: url, download: filename });
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}
